import { reactive } from 'vue'
import { getAuth } from 'firebase/auth'
import { doc, getDocFromServer } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '@/firebase'
import {
  IMPORT_ACTIVITY,
  describeImportActivity,
  importOutcomeToken
} from '@/utils/studentImportOutcome.js'
import { bulkScopeEvent, createImportActivityLog, parsedFileEvent } from './importActivityLog.js'

const enabled =
  import.meta.env.VITE_FIREBASE_PROJECT_ID === 'casemangervue' &&
  import.meta.env.VITE_PROD_HARDENED !== true
export const importActivityState = reactive({ status: 'idle', runId: '', savedEvents: 0 })
let log
let epoch = 0
const tracked = new Map()
const outcomeChecks = new Map()
const FIELD_PATHS = {
  instruction: 'accommodations.instruction',
  assessment: 'accommodations.assessment',
  reviewDate: 'dates.reviewDate',
  primaryDisability: 'studentData.primaryDisability',
  plan: 'studentData.plan',
  planStatus: 'studentData.planStatus',
  caseManagerId: 'studentData.caseManagerId',
  flag1: 'flags.flag1',
  flag2: 'flags.flag2'
}
const SERVER_PATHS = [
  ...Object.values(FIELD_PATHS),
  'flags.separateSetting',
  'dates.meetingDate',
  'dates.reevalDate'
]
const valueAt = (data, path) => path.split('.').reduce((v, key) => v?.[key], data)
const hasPath = (data, path) => {
  const keys = path.split('.')
  const last = keys.pop()
  const parent = keys.reduce((v, key) => v?.[key], data)
  return !!parent && Object.prototype.hasOwnProperty.call(parent, last)
}

function logger() {
  if (!enabled) return null
  if (!log) {
    let storage
    try {
      storage = window.sessionStorage
    } catch {
      /* optional recovery */
    }
    const save = httpsCallable(functions, 'saveSmokeReport', { timeout: 15000 })
    log = createImportActivityLog({
      send: async (payload) => (await save(payload)).data,
      getOwner: () => getAuth().currentUser?.uid,
      buildId: `${import.meta.env.VITE_APP_VERSION}-${import.meta.url.split('/').pop()}`,
      storage,
      onStatus: (status) => Object.assign(importActivityState, status)
    })
    window.addEventListener('online', () => {
      void log.flush()
    })
  }
  return log
}

export function startImportActivity() {
  const journal = logger()
  if (!journal) return
  if (!journal.hasSession()) journal.record('started')
}
export function hasImportActivity() {
  try {
    return !!logger()?.hasSession()
  } catch {
    return false
  }
}
export function logImportActivity(event, values, status) {
  const journal = logger()
  if (journal?.hasSession()) journal.record(event, values, status)
}
export function logParsedFile(source, detail = {}) {
  startImportActivity()
  logImportActivity('file_selected', { source })
  const entry = parsedFileEvent(source, detail)
  logImportActivity(entry.event, entry.values, entry.status)
}
export function logPreviewToggle(source, previewOpen) {
  logImportActivity('preview_toggled', { source, previewOpen: !!previewOpen })
}
export function logBulkScope(decision, applied, scope) {
  const entry = bulkScopeEvent(applied, scope)
  logImportActivity('bulk_scope', { decision, ...entry.values }, entry.status)
}
export function retryImportActivity() {
  return logger()?.flush()
}
export function restartImportActivity() {
  return logger()?.restart()
}

export function observeImportLayout(scroll) {
  if (!enabled || !logger()?.hasSession() || !scroll) return
  requestAnimationFrame(() => {
    if (!scroll.isConnected || !scroll.getClientRects().length) return
    // Sources stay mounted together in .wizard-frame. Only inspect the
    // wizard that owns this scroll area, not another source's hidden controls.
    const root = scroll.parentElement
    const actions = root.querySelector('.wizard-actions')?.getBoundingClientRect()
    const step = root.querySelector('.step-item.active')?.getBoundingClientRect()
    const inView = (r) =>
      !!r &&
      r.height > 0 &&
      r.top >= 0 &&
      r.bottom <= window.innerHeight &&
      r.left >= 0 &&
      r.right <= window.innerWidth
    const actionsVisible = inView(actions),
      stepVisible = inView(step)
    const reviewHeight = Math.max(0, Math.round(scroll.getBoundingClientRect().height))
    logImportActivity(
      'layout',
      {
        actionsVisible,
        stepVisible,
        reviewHeight,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
      },
      actionsVisible && stepVisible && reviewHeight > 0 ? 'pass' : 'blocked'
    )
  })
}

async function readServerDocument(reference) {
  let timer
  try {
    return await Promise.race([
      getDocFromServer(reference),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), 10000)
      })
    ])
  } finally {
    clearTimeout(timer)
  }
}

// Compare only the displayed aggregate outcome. Neither school identifiers
// nor stored values are sent to the diagnostic service.
function outcomeSnapshot(settings, source) {
  // Firestore merge retains keys from previous operations: an import result
  // may still contain restored, and Undo may retain updated/created. Compare
  // the same status, timestamp and applicable counts that the overview uses.
  return JSON.stringify({
    outcome: importOutcomeToken(settings, source),
    ...describeImportActivity(settings, source)
  })
}

export async function observeImportOutcome({ source, schoolId, settings, isCurrent }) {
  if (!enabled || !logger()?.hasSession() || !IMPORT_ACTIVITY[source]) return
  const owner = getAuth().currentUser?.uid
  const generation = Symbol()
  outcomeChecks.set(source, generation)
  const current = () => outcomeChecks.get(source) === generation && isCurrent()
  if (!schoolId) {
    logImportActivity('outcome_unavailable', { source }, 'blocked')
    return
  }
  const expected = outcomeSnapshot(settings, source)
  try {
    // Bypass the effective-settings cache and offline fallback. The requested
    // document path is the evidence of school scope, not a hardcoded flag.
    const snapshot = await readServerDocument(doc(db, 'schools', schoolId))
    if (owner !== getAuth().currentUser?.uid) return
    if (!current()) {
      logImportActivity('outcome_superseded', { source }, 'skip')
      return
    }
    const stored = snapshot.data() || {}
    const outcome = importOutcomeToken(stored, source)
    const matches = outcomeSnapshot(stored, source) === expected
    logImportActivity(
      'outcome_readback',
      { source, outcome, matches },
      !matches ? 'fail' : ['missing', 'unrecorded'].includes(outcome) ? 'skip' : 'pass'
    )
  } catch {
    if (owner !== getAuth().currentUser?.uid) return
    logImportActivity(
      current() ? 'outcome_unavailable' : 'outcome_superseded',
      { source },
      current() ? 'blocked' : 'skip'
    )
  }
}

// Snapshot expected values only in memory. Firebase receives counts/results.
// Read-backs run separately and never delay or reject the user's write.
export function createImportWriteObserver(operation) {
  if (!enabled || !logger()?.hasSession()) return undefined
  const generation = ++epoch
  const owner = getAuth().currentUser?.uid
  let checked = 0
  return ({ docId, previous, applied }) => {
    if (owner !== getAuth().currentUser?.uid) return
    if (++checked > 25) {
      if (checked === 26)
        logImportActivity('readback_unavailable', { operation, students: 25 }, 'skip')
      return
    }
    const before = previous?.app || {},
      patch = applied?.app || {}
    const expected = Object.fromEntries(
      SERVER_PATHS.map((path) => [
        path,
        hasPath(patch, path) ? valueAt(patch, path) : valueAt(before, path)
      ])
    )
    const formExpected = Object.fromEntries(
      Object.entries(FIELD_PATHS)
        .filter(([, path]) => hasPath(patch, path))
        .map(([key, path]) => [key, valueAt(patch, path)])
    )
    tracked.set(docId, { owner, operation, formExpected })
    // Bound data retained in memory, too.
    if (tracked.size > 25) tracked.delete(tracked.keys().next().value)
    void (async () => {
      try {
        const snapshot = await readServerDocument(doc(db, 'students', docId))
        if (owner !== getAuth().currentUser?.uid) return
        if (generation !== epoch) {
          logImportActivity('readback_superseded', { operation }, 'skip')
          return
        }
        const mismatches = SERVER_PATHS.filter(
          (path) => valueAt(snapshot.data()?.app, path) !== expected[path]
        ).length
        logImportActivity(
          'write_readback',
          { operation, fields: SERVER_PATHS.length, mismatches },
          snapshot.exists() && !mismatches ? 'pass' : 'fail'
        )
      } catch {
        if (owner === getAuth().currentUser?.uid)
          logImportActivity('readback_unavailable', { operation }, 'blocked')
      }
    })()
  }
}

export function observeImportStudentOpen(studentId, form) {
  if (!enabled || !logger()?.hasSession()) return
  const expected = tracked.get(studentId)
  if (expected && expected.owner !== getAuth().currentUser?.uid) return
  logImportActivity('student_opened', { tracked: !!expected })
  if (!expected) return // A refresh discards private in-memory comparisons.
  const fields = Object.entries(expected.formExpected)
  const mismatches = fields.filter(([key, value]) => form[key] !== (value ?? '')).length
  logImportActivity(
    'editor_values',
    { operation: expected.operation, fields: fields.length, mismatches },
    mismatches ? 'fail' : 'pass'
  )
}
